const FRAMES_END = 0.8;
const FRAME_WIDTHS = [768, 960];
const MOBILE_MAX_WIDTH = 768;
const MOBILE_FROM = 0.8;
const MOBILE_TO = 0.25;
const LOAD_STRIDES = [8, 4, 2, 1];
const LOAD_PARALLEL = 6;
const LOOKAHEAD = '300% 0px';
const MOBILE_LOOKAHEAD = '150% 0px';
const EASE = 0.18;

const whenIdle = new Promise((resolve) => {
  if ('requestIdleCallback' in window) window.requestIdleCallback(() => resolve(), { timeout: 800 });
  else setTimeout(resolve, 300);
});

class DrvOne01 extends HTMLElement {
  connectedCallback() {
    this.sticky = this.querySelector('.drv-101__sticky');
    this.stage = this.querySelector('.drv-101__stage');
    this.finale = this.querySelector('.drv-101__scene--d');
    this.hint = this.querySelector('.drv-101__scrolldown');
    this.canvas = this.querySelector('.drv-101__canvas');
    if (!this.sticky || !this.stage || !this.finale) return;
    this.desktop = window.matchMedia('(min-width: 1000px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.canvas && navigator.connection?.saveData) {
      this.setAttribute('data-static', '');
      this.classList.add('drv-101--static');
    }
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.onResize = () => this.fit();
    document.addEventListener('scroll', this.onScroll, true);
    window.addEventListener('resize', this.onResize);
    if (this.canvas) {
      this.count = Number(this.canvas.dataset.count) || 0;
      this.observer = new IntersectionObserver(
        (entries) => {
          this.near = entries[entries.length - 1].isIntersecting;
          this.load();
        },
        { root: this.scrollRoot(), rootMargin: this.desktop.matches ? LOOKAHEAD : MOBILE_LOOKAHEAD }
      );
      this.observer.observe(this);
    }
    this.fit();
  }

  disconnectedCallback() {
    if (!this.onScroll) return;
    document.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.onResize);
    this.observer?.disconnect();
    cancelAnimationFrame(this.raf);
    this.queue = [];
  }

  scrollRoot() {
    for (let el = this.parentElement; el && el !== document.body; el = el.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(el).overflowY)) return el;
    }
    return null;
  }

  animated() {
    return Boolean(this.canvas) && !this.hasAttribute('data-static') && !this.reduced.matches;
  }

  fit() {
    this.style.removeProperty('--drv-101-zoom');
    if (this.desktop.matches) {
      const frameW = this.stage.offsetWidth;
      const stageH = this.stage.offsetHeight;
      const byWidth = frameW ? (this.clientWidth || window.innerWidth) / frameW : 1;
      const byHeight = stageH ? window.innerHeight / stageH : 1;
      const z = Math.min(1, byWidth, byHeight);
      if (z < 1) this.style.setProperty('--drv-101-zoom', String(z));
    }
    this.load();
    this.update();
  }

  load() {
    if (this.frames || this.waiting || !this.near || !this.animated()) return;
    if (this.count < 2 || !this.canvas.dataset.src) return;
    this.waiting = true;
    whenIdle.then(() => this.start());
  }

  start() {
    this.waiting = false;
    if (this.frames || !this.near || !this.animated()) return;
    const need = this.canvas.getBoundingClientRect().width * (window.devicePixelRatio || 1);
    const widths = this.desktop.matches ? FRAME_WIDTHS : FRAME_WIDTHS.filter((w) => w <= MOBILE_MAX_WIDTH);
    this.frameWidth = widths.find((w) => w >= need) || widths[widths.length - 1];
    this.frames = new Array(this.count);
    this.drawn = -1;
    const keep = (i) => this.desktop.matches || i % 2 === 0;
    const order = [0, this.count - 1];
    LOAD_STRIDES.forEach((stride) => {
      for (let i = stride; i < this.count - 1; i += stride) {
        if (keep(i) && !order.includes(i)) order.push(i);
      }
    });
    this.firstPass = order.filter((i) => this.inFirstPass(i)).length;
    this.firstDone = 0;
    this.style.setProperty('--drv-101-progress', '0');
    this.classList.add('is-loading');
    this.queue = order;
    for (let k = 0; k < LOAD_PARALLEL; k += 1) this.fetchNext();
  }

  inFirstPass(i) {
    return i % LOAD_STRIDES[0] === 0 || i === this.count - 1;
  }

  fetchNext() {
    const i = this.queue.shift();
    if (i === undefined) return;
    const img = new Image();
    img.decoding = 'async';
    img.fetchPriority = 'low';
    img.src = this.frameUrl(i);
    img
      .decode()
      .then(
        () => {
          this.frames[i] = img;
        },
        () => {}
      )
      .finally(() => {
        this.loaded(i);
        this.fetchNext();
      });
  }

  loaded(i) {
    if (this.inFirstPass(i)) {
      this.firstDone += 1;
      this.style.setProperty('--drv-101-progress', String(this.firstDone / this.firstPass));
      if (this.firstDone >= this.firstPass) this.classList.remove('is-loading');
    }
    if (this.pos !== undefined) this.draw(Math.round(this.pos));
  }

  frameUrl(i) {
    const url = new URL(this.canvas.dataset.src, window.location.href);
    const at = url.pathname.lastIndexOf('001');
    url.pathname = `${url.pathname.slice(0, at)}${String(i + 1).padStart(3, '0')}${url.pathname.slice(at + 3)}`;
    url.searchParams.set('width', String(this.frameWidth));
    return url.href;
  }

  draw(target) {
    if (!this.frames) return;
    let k = -1;
    for (let d = 0; d < this.count && k < 0; d += 1) {
      if (this.frames[target - d]) k = target - d;
      else if (this.frames[target + d]) k = target + d;
    }
    if (k < 0 || k === this.drawn) return;
    const img = this.frames[k];
    if (this.canvas.width !== img.naturalWidth || this.canvas.height !== img.naturalHeight) {
      this.canvas.width = img.naturalWidth;
      this.canvas.height = img.naturalHeight;
    }
    this.canvas.getContext('2d').drawImage(img, 0, 0);
    if (this.drawn < 0) this.canvas.parentElement.classList.add('is-drawn');
    this.drawn = k;
  }

  ease() {
    if (this.raf) return;
    let last = performance.now();
    const step = (now) => {
      const k = 1 - (1 - EASE) ** Math.min(4, Math.max(0, now - last) / 16.67);
      last = now;
      const diff = this.target - this.pos;
      this.pos = Math.abs(diff) < 0.05 ? this.target : this.pos + diff * k;
      this.draw(Math.round(this.pos));
      this.raf = this.pos === this.target ? 0 : requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  update() {
    if (!this.animated()) {
      this.classList.remove('is-scrub', 'is-done');
      this.finale.classList.toggle('is-active', this.desktop.matches);
      return;
    }
    let p;
    if (this.desktop.matches) {
      this.classList.remove('is-scrub', 'is-done');
      const rect = this.getBoundingClientRect();
      const range = Math.max(1, rect.height - this.sticky.getBoundingClientRect().height);
      const scrolled = Math.min(1, Math.max(0, -rect.top / range));
      const done = scrolled >= FRAMES_END;
      this.finale.classList.toggle('is-active', done);
      if (this.hint) this.hint.classList.toggle('is-hidden', done);
      p = Math.min(scrolled, FRAMES_END) / FRAMES_END;
    } else {
      this.classList.add('is-scrub');
      this.finale.classList.remove('is-active');
      const top = this.canvas.parentElement.getBoundingClientRect().top;
      const vh = window.innerHeight;
      p = Math.min(1, Math.max(0, (vh * MOBILE_FROM - top) / (vh * (MOBILE_FROM - MOBILE_TO))));
      this.classList.toggle('is-done', p >= 1);
    }
    this.target = p * (this.count - 1);
    if (this.pos === undefined) this.pos = this.target;
    this.ease();
  }
}

if (!customElements.get('drv-one01')) {
  customElements.define('drv-one01', DrvOne01);
}
